import {
  ITEM_LEGACY_DOC_CONVERSION_ATTEMPTED_EVENT_NAME,
  LEGACY_DOCUMENT_REMEDIATION_MODAL_PRESENTED_TO_CUSTOMER,
  LEGACY_EDIT_DOCUMENT_URL_CONVERSION_SUCCESS_EVENT_NAME,
  ORDER_ITEMS_REORDERED_ATTEMPTED_EVENT_NAME,
  PRODUCT_REORDERED_LABEL,
} from '../constants/newRelic';
import {
  ORDER_HISTORY_PAGE_NAME,
  BUY_IT_AGAIN_PAGE_NAME,
} from '../constants/sourcePage';
import {
  BUY_IT_AGAIN_PAGE_ZONE,
  EDIT_DESIGN_AND_REORDER_CTA_VALUE,
  MY_ACCOUNT_PAGE_SECTION,
  MY_ACCOUNT_PAGE_STAGE,
  NAVIGATION_CLICKED_EVENT_NAME,
  REORDER_PRODUCT_ADDED_TO_CART_EVENT_NAME,
  STUDIO_DESTINATION_PAGE_PATH,
  BUTTON_CLICKED_EVENT_NAME,
} from '../constants/tracking';
import type { LineItemData } from '../types/OrderHistory.types';
import type { PricedQuantity } from '../types/ReorderableItems.types';
import type { SourcePage } from '../types/sourcePage.types';
import { createVariantFromProductAttrs } from '../utils/productVariantCreator';
import { trackAnalyticsError } from './newrelic/newRelicTrackingApi';
import {
  FLAWED_UX_EVENT_NAME,
  normalizeFlawedEvent,
  type NormalizedFlawedEventType,
  type RawFlawedEventType,
} from '@vp/flawed-ux-tracking';

type Tracking = {
  track?: (...args: unknown[]) => void;
  page?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    tracking?: Tracking;
  }
}

/**
 * Protect against tracking not yet being installed on page or window not existing (SSR).
 * The `tracking.js` package will fire a `trackingReady` event when tracking is
 * on the page.  Each `callWhenReadyFn` is a single-shot and removed once executed.
 * Note we use this to check for both 'track' and 'page' functions, since they in the same module.
 */
function onTrackingReady(callback: () => void) {
  if (window) {
    if (window.tracking && window.tracking.track) {
      callback();
    } else {
      const callWhenReadyFn = () => {
        callback();
        window.removeEventListener('trackingReady', callWhenReadyFn);
      };
      window.addEventListener('trackingReady', callWhenReadyFn);
    }
  }
}

/**
 * Actually send `track` event. Assumes `window.tracking.track` exists.
 */
function sendTrackingEvent(
  name: string,
  data: object & { shopperId?: string; shopper_id?: string }
) {
  try {
    // @ts-expect-error - window.tracking.track should be available after onTrackingReady
    window.tracking.track(name, data);
  } catch (error) {
    if (error instanceof Error) trackAnalyticsError(error, data);
    console.error(
      "[TrackingJS] Error occured when firing a 'track' event",
      error
    );
  }
}

/** Execute or queue up a track call
 * If environment variable TRACKING_OVERRIDE_BEHAVIOR === 'log' we will log the event
 * in the console and early out before firing the actual 'track()'. This allows us to test
 * changes to events locally without polluting analytics data.
 */
function track(name: string, data: object) {
  if (process.env.TRACKING_OVERRIDE_BEHAVIOR === 'log') {
    // log and early out
    console.log('[Mock Tracking™]: Track()', name, data);
    return;
  }
  onTrackingReady(() => sendTrackingEvent(name, data));
}

interface PageEventData {
  pageTrackingName: string;
  pageTrackingSectionName: string;
  pageTrackingStageName: string;
  shopperId: string;
  additionalData?: object;
}

/**
 * Actually send `page` event. Assumes `window.tracking.page` exists.
 */
function sendPageEvent({
  pageTrackingName,
  pageTrackingSectionName,
  pageTrackingStageName,
  shopperId,
  additionalData,
}: PageEventData) {
  const pageData = {
    pageName: pageTrackingName,
    pageSection: pageTrackingSectionName,
    pageStage: pageTrackingStageName,
    shopperId,
    additionalData: additionalData || {},
  };
  try {
    if (window.tracking?.page) {
      window.tracking.page(pageData);
    }
  } catch (error) {
    if (error instanceof Error) {
      trackAnalyticsError(error, pageData);
    }
    console.error(
      "[TrackingJS] Error occured when firing a 'page' event",
      error
    );
  }
}

/** Execute or queue up a 'page' tracking call. */
function pageViewed({
  pageTrackingName,
  pageTrackingSectionName,
  pageTrackingStageName,
  shopperId,
  additionalData,
}: PageEventData) {
  onTrackingReady(() =>
    sendPageEvent({
      pageTrackingName,
      pageTrackingSectionName,
      pageTrackingStageName,
      shopperId,
      additionalData,
    })
  );
}

// ModFather tracking support:
// ModFather expects an object with a 'trackEvent' function that takes an event.
// This adapter appends the route, which is needed by Segment, and the event
// name, which determines the Snowflake table that receives the event data.
const MOD_EVENT_NAME = 'UX_MOD_EVENT';
const MOD_ROUTE = 'om-frontend';

export const modFatherTracker = (event: {
  eventType: string;
  route: string;
}) => {
  if (!event || !event.eventType) {
    return;
  }
  event.route = MOD_ROUTE;
  track(MOD_EVENT_NAME, event);
};

export function pageViewOrderHistory(shopperId: string) {
  pageViewed({
    pageTrackingName: ORDER_HISTORY_PAGE_NAME,
    pageTrackingSectionName: MY_ACCOUNT_PAGE_SECTION,
    pageTrackingStageName: MY_ACCOUNT_PAGE_STAGE,
    shopperId,
  });
}

export function pageViewBuyItAgain(shopperId: string) {
  pageViewed({
    pageTrackingName: BUY_IT_AGAIN_PAGE_NAME,
    pageTrackingSectionName: MY_ACCOUNT_PAGE_SECTION,
    pageTrackingStageName: MY_ACCOUNT_PAGE_STAGE,
    shopperId,
  });
}

interface TrackOrderActionArgs {
  actionName: string;
  additionalData: object;
}

// TODO: Revisit this for NRQL integration.
// Could be used to track # of orders canceled, reorders placed, and any other actions a user can take on the page.
export const trackOrderAction = (props: TrackOrderActionArgs) => {
  const { actionName, additionalData } = props;
  track(actionName, additionalData);
};

/**
 * Fire an event that tracks when a user clicks the 'Edit design & reorder' button from the "Buy It Again Page" and is navigated to studio
 *
 * @param {object} additionalData
 */
export function trackBuyItAgainEditDesignAndReorderClicked() {
  const additionalData = {
    category: MY_ACCOUNT_PAGE_SECTION,
    label: 'Content: Buy It Again',
    eventDetail: `${MY_ACCOUNT_PAGE_SECTION};${STUDIO_DESTINATION_PAGE_PATH};${BUY_IT_AGAIN_PAGE_ZONE};${EDIT_DESIGN_AND_REORDER_CTA_VALUE}`,
    navigationDetail: EDIT_DESIGN_AND_REORDER_CTA_VALUE,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName: BUY_IT_AGAIN_PAGE_NAME,
  };

  trackOrderAction({
    actionName: NAVIGATION_CLICKED_EVENT_NAME,
    additionalData,
  });
}

/**
 * Fire an event that states this orderNumber was attempted to be reordered, with these lineItems being part of
 * that attempt.
 *
 * This method will fire an event for EACH lineItem ID
 * @param {string} orderNum
 * @param {*} reorderableItem // TODO: fix type
 */
export function orderReorderV2Attempted(
  orderNum: string,
  reorderItems: { lineItemId: string; quantity: number }[]
) {
  if (reorderItems) {
    reorderItems.forEach((reorderItem) => {
      const reorderItemData = {
        orderNumber: orderNum,
        lineItemId: reorderItem.lineItemId,
        quantity: reorderItem.quantity,
      };
      trackOrderAction({
        actionName: ORDER_ITEMS_REORDERED_ATTEMPTED_EVENT_NAME,
        additionalData: reorderItemData,
      });
    });
  }
}

/**
 * Fire an event that tracks when a user clicks the reorder button
 * occurs after the re-order comes back as a successful operation (i.e. item has been added to cart successfully).
 *
 * @param {object} additionalData
 */
export function trackReorderProductAddedToCart(
  reorderableItem: LineItemData,
  sourcePage: SourcePage
) {
  const additionalData = {
    label: PRODUCT_REORDERED_LABEL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName: `My Account: ${sourcePage}`,
    product_id: reorderableItem?.purcsData?.mpvId || reorderableItem.id,
    originalLineItemId: reorderableItem.id,
    name: reorderableItem.productName,
    core_product_id: reorderableItem.productKey,
    is_reordered: true,
    price: reorderableItem.pricing.totalDiscountedPrice?.centAmount,
    list_price: reorderableItem.pricing.totalListPrice?.centAmount,
    quantity: 1,
    sales_quantity: reorderableItem.quantity,
    discount:
      typeof reorderableItem?.pricing.totalListPrice?.centAmount === 'number' &&
      typeof reorderableItem?.pricing.totalDiscountedPrice?.centAmount ===
        'number'
        ? reorderableItem.pricing.totalListPrice.centAmount -
          reorderableItem.pricing.totalDiscountedPrice.centAmount
        : null,
    variant: createVariantFromProductAttrs(reorderableItem.attributes),
  };

  trackOrderAction({
    actionName: REORDER_PRODUCT_ADDED_TO_CART_EVENT_NAME,
    additionalData,
  });
}

/**
 * Fire an event that tracks when a user clicks the reorder button from the "Buy It Again Page"
 * occurs after the re-order comes back as a successful operation (i.e. item has been added to cart successfully).
 *
 * @param {LineItemData} item
 * @param {PricedQuantity} pricedQuantity. The priced quantity object that was selected by the user when adding to cart. Otherwise, null.
 */
export function trackBuyItAgainReorderProductAddedToCart({
  item,
  pricedQuantity,
}: {
  item: LineItemData;
  pricedQuantity: PricedQuantity;
}) {
  const additionalData = {
    label: PRODUCT_REORDERED_LABEL,
    pageSection: MY_ACCOUNT_PAGE_SECTION,
    pageStage: MY_ACCOUNT_PAGE_STAGE,
    pageName: BUY_IT_AGAIN_PAGE_NAME,
    product_id: item.purcsData?.mpvId || item.id,
    originalLineItemId: item.id,
    name: item.productName,
    core_product_id: item.productKey,
    is_reordered: true,
    // TODO - we may want to have decimal prices instead of cent amount here; for now this is fine, but we will verify with Hasnene
    price: pricedQuantity.discountedPriceCents?.untaxed,
    list_price: pricedQuantity.listPriceCents?.untaxed,
    quantity: 1,
    sales_quantity: pricedQuantity.quantity,
    discount:
      typeof pricedQuantity?.listPriceCents?.untaxed === 'number' &&
      typeof pricedQuantity?.discountedPriceCents?.untaxed === 'number'
        ? pricedQuantity.listPriceCents.untaxed -
          pricedQuantity.discountedPriceCents.untaxed
        : null,
    variant: createVariantFromProductAttrs(item.attributes),
  };

  trackOrderAction({
    actionName: REORDER_PRODUCT_ADDED_TO_CART_EVENT_NAME,
    additionalData,
  });
}

/**
 * Fire an event that states that converting a legacy document was attempted
 *
 * @param {object} additionalData
 */
export function convertLegacyDocumentAttempted(additionalData: object) {
  trackOrderAction({
    actionName: ITEM_LEGACY_DOC_CONVERSION_ATTEMPTED_EVENT_NAME,
    additionalData,
  });
}

/**
 * Fire an event that states a customer was presented with an option to fix their
 * failed legacy doc
 *
 * @param {object} additionalData
 */
export function docRemediationModalPresentedToCustomer(additionalData: object) {
  trackOrderAction({
    actionName: LEGACY_DOCUMENT_REMEDIATION_MODAL_PRESENTED_TO_CUSTOMER,
    additionalData,
  });
}

/**
 * Fire an event that tracks when a user clicks the edit button for a migrated order
 * occurs after the doc conversion call comes back as a successful operation (i.e. editDocumentUrl is present on the response).
 *
 * @param {object} additionalData
 */
export function trackLegacyEditDocumentUrlConversionSuccess(
  lineItem: LineItemData,
  sourcePage: SourcePage
) {
  const additionalData = {
    label: 'Legacy Edit URL returned from DocGateway',
    pageSection: 'My Account',
    pageStage: 'My Account',
    pageName: `My Account: ${sourcePage}`,
    product_id: lineItem.id,
    name: lineItem.productName,
    core_product_id: lineItem.productKey,
    price: lineItem.pricing.totalDiscountedPrice?.centAmount,
    list_price: lineItem.pricing.totalListPrice?.centAmount,
    quantity: 1,
    sales_quantity: lineItem.quantity,
    discount:
      typeof lineItem?.pricing?.totalListPrice?.centAmount === 'number' &&
      typeof lineItem?.pricing?.totalDiscountedPrice?.centAmount === 'number'
        ? lineItem.pricing.totalListPrice.centAmount -
          lineItem.pricing.totalDiscountedPrice.centAmount
        : null,
    variant: createVariantFromProductAttrs(lineItem.attributes),
  };

  trackOrderAction({
    actionName: LEGACY_EDIT_DOCUMENT_URL_CONVERSION_SUCCESS_EVENT_NAME,
    additionalData,
  });
}

/**
 * Fire an event that states this orderNumber was attempted to be bought now, with these lineItems being part of
 * that attempt.
 *
 * This method will fire an event for EACH lineItem ID
 * @param {string} orderNum
 * @param {array} reorderItems
 * @param {string} pageName the name of the page to include in tracking details
 */
export function expressCheckoutBuyNowAttempted(
  orderNum: string,
  reorderItems: {
    lineItemId: string;
    quantity: number;
    analytics: { source: string };
    displayPosition: number;
    productId: string;
  }[],
  pageName: string
) {
  if (reorderItems) {
    reorderItems.forEach((reorderItem) => {
      const { displayPosition, productId } = reorderItem;
      const eventData = {
        category: pageName,
        label: productId,
        eventDetail: `${productId}:${displayPosition}`,
        navigationDetail: 'Buy now',
        orderNumber: orderNum,
      };
      trackOrderAction({
        actionName: BUTTON_CLICKED_EVENT_NAME,
        additionalData: eventData,
      });
    });
  }
}

export const trackFlawedUxEvent = (rawEventData: RawFlawedEventType) => {
  const eventData: NormalizedFlawedEventType =
    normalizeFlawedEvent(rawEventData);
  track(FLAWED_UX_EVENT_NAME, eventData);
};
